import { z } from 'zod'
import type { SupplierCategory } from './supplier'
import { planningTimeSchema } from './supplier-plan'

export const RECEPTION_PART_TYPES = [
  'toast',
  'beforeCeremony',
  'reception',
  'banquet',
  'cake',
  'sweetBar',
  'bar',
  'coffee',
  'lateNight',
  'favors',
  'vendorMeals',
  'custom',
] as const
export const RECEPTION_PROVIDERS = ['venue', 'supplier', 'self', 'unknown'] as const
export const RECEPTION_SERVINGS = ['table', 'buffet', 'stations', 'passed', 'self', 'bar'] as const
export const RECEPTION_ALCOHOL_POLICIES = ['open', 'limited', 'none', 'unknown'] as const
/** Supplier row ids of the former fixed slots; Harmonogram overrides are keyed by them. */
export const LEGACY_RECEPTION_SUPPLIER_IDS = ['catering', 'bar', 'cake'] as const
/** The toast part keeps the Harmonogram event id of the former welcome toast toggle. */
export const WELCOME_TOAST_PART_ID = 'welcome-toast'
export const RECEPTION_PART_LIMIT = 40
export const RECEPTION_DRINKS = ['wine', 'beer', 'spirits', 'soft'] as const

export const receptionPartTypeSchema = z.enum(RECEPTION_PART_TYPES)
export type ReceptionPartType = z.infer<typeof receptionPartTypeSchema>
export type ReceptionProvider = (typeof RECEPTION_PROVIDERS)[number]
export type ReceptionServing = (typeof RECEPTION_SERVINGS)[number]
export type ReceptionAlcoholPolicy = (typeof RECEPTION_ALCOHOL_POLICIES)[number]

/** Supplier category offered when picking or creating a supplier, and the serving styles that fit. */
export const receptionPartTypes = {
  toast: { category: 'catering', serving: ['passed', 'self', 'bar'] },
  beforeCeremony: { category: 'catering', serving: ['self', 'passed', 'buffet'] },
  reception: { category: 'catering', serving: ['buffet', 'stations', 'passed'] },
  banquet: { category: 'catering', serving: ['table', 'buffet', 'stations', 'passed'] },
  cake: { category: 'cake-sweets', serving: ['passed', 'self', 'table'] },
  sweetBar: { category: 'cake-sweets', serving: ['self', 'passed', 'stations'] },
  bar: { category: 'bar', serving: ['bar', 'self', 'passed'] },
  coffee: { category: 'catering', serving: ['self', 'bar', 'passed'] },
  lateNight: { category: 'catering', serving: ['buffet', 'passed', 'stations'] },
  favors: { category: 'cake-sweets', serving: ['self', 'table', 'passed'] },
  vendorMeals: { category: 'catering', serving: ['table', 'buffet', 'self'] },
  custom: { category: 'other', serving: ['table', 'buffet', 'passed'] },
} as const satisfies Record<
  ReceptionPartType,
  { category: ReceptionSupplierCategory; serving: readonly ReceptionServing[] }
>
export type ReceptionSupplierCategory = Exclude<SupplierCategory, 'venue'>

/**
 * A wedding supplier fits a category when one of its categories or roles is that category;
 * `other` accepts any supplier with a job besides the venue.
 */
export function supplierFitsReceptionCategory(
  category: ReceptionSupplierCategory,
  supplier: { categories: readonly string[]; roles: readonly string[] },
) {
  const jobs = [...supplier.categories, ...supplier.roles]
  return category === 'other' ? jobs.some((job) => job !== 'venue') : jobs.includes(category)
}

const partSupplierSchema = z.object({
  id: z.uuid().or(z.enum(LEGACY_RECEPTION_SUPPLIER_IDS)),
  weddingSupplierId: z.uuid().optional(),
  /** Snapshot of the supplier's name for the schedule and exports. */
  name: z.string().trim().max(300),
  arrival: planningTimeSchema,
})
const partLineSchema = z.object({ id: z.uuid(), text: z.string().trim().max(500) })
const drinkCount = z.number().int().min(0).max(100_000).default(0)
/** The couple's own drink estimate for a bar; without it the planner shows its suggestion. */
const drinksSchema = z.object({
  wine: drinkCount,
  beer: drinkCount,
  spirits: drinkCount,
  soft: drinkCount,
})
const receptionPartSchema = z
  .object({
    id: z.uuid().or(z.literal(WELCOME_TOAST_PART_ID)),
    type: receptionPartTypeSchema,
    name: z.string().trim().max(300),
    time: planningTimeSchema,
    timeEnd: planningTimeSchema.default(''),
    place: z.string().trim().max(300).default(''),
    guestVisible: z.boolean().optional(),
    provider: z.enum(RECEPTION_PROVIDERS).nullable(),
    suppliers: z.array(partSupplierSchema).max(20),
    serving: z.enum(RECEPTION_SERVINGS).nullable(),
    lines: z.array(partLineSchema).max(100),
    notes: z.string().trim().max(10_000),
    alcohol: z.enum(RECEPTION_ALCOHOL_POLICIES).nullable().default(null),
    drinks: drinksSchema.nullable().default(null),
  })
  .transform(({ guestVisible, ...part }) => ({
    ...part,
    guestVisible: guestVisible ?? part.type === 'toast',
  }))
  .refine(
    (part) =>
      part.serving === null ||
      receptionPartTypes[part.type].serving.some((serving) => serving === part.serving),
    { path: ['serving'], params: { reason: 'serving-not-allowed' } },
  )
  .refine((part) => part.alcohol === null || part.type === 'bar', {
    path: ['alcohol'],
    params: { reason: 'alcohol-bar-only' },
  })
  .refine((part) => part.drinks === null || part.type === 'bar', {
    path: ['drinks'],
    params: { reason: 'drinks-bar-only' },
  })
  .refine((part) => part.id !== WELCOME_TOAST_PART_ID || part.type === 'toast', {
    path: ['id'],
    params: { reason: 'welcome-toast-type' },
  })

export const receptionSchema = z
  .object({
    parts: z.array(receptionPartSchema).max(RECEPTION_PART_LIMIT),
    notes: z.string().trim().max(5000),
    hiddenPromptCodes: z.array(z.string().trim().min(1).max(80)).default([]),
  })
  .refine(
    (plan) => {
      const ids = plan.parts.flatMap((part) => [
        part.id,
        ...part.suppliers.map((supplier) => supplier.id),
      ])
      return new Set(ids).size === ids.length
    },
    { path: ['parts'], params: { reason: 'duplicate-id' } },
  )
  .transform((plan) => ({ ...plan, hiddenPromptCodes: liveHiddenPromptCodes(plan) }))
  .refine((plan) => plan.hiddenPromptCodes.length <= 100, {
    path: ['hiddenPromptCodes'],
    params: { reason: 'too-many' },
  })
export type ReceptionInput = z.infer<typeof receptionSchema>
export type ReceptionPart = ReceptionInput['parts'][number]
export type ReceptionPartSupplier = ReceptionPart['suppliers'][number]
export type ReceptionDrinks = NonNullable<ReceptionPart['drinks']>

/** Hidden codes without duplicates and without those of removed parts (`cake-`) or supplier rows (`arrival-`). */
function liveHiddenPromptCodes(plan: {
  parts: readonly { id: string; suppliers: readonly { id: string }[] }[]
  hiddenPromptCodes: readonly string[]
}) {
  const partIds = new Set(plan.parts.map((part) => part.id))
  const rowIds = new Set(plan.parts.flatMap((part) => part.suppliers.map((row) => row.id)))
  return [...new Set(plan.hiddenPromptCodes)].filter((code) => {
    if (code.startsWith('cake-')) return partIds.has(code.slice('cake-'.length))
    if (code.startsWith('arrival-')) return rowIds.has(code.slice('arrival-'.length))
    return true
  })
}

export function emptyReception(): ReceptionInput {
  return { parts: [], notes: '', hiddenPromptCodes: [] }
}
